<template>
  <Dialog v-model:visible="visible" :modal="true" :showHeader="false" :closable="false" :dismissableMask="true"
    :style="{ width: '600px', maxWidth: '90vw' }"
    class="command-palette-dialog" contentClass="p-0" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="command-palette-container surface-card">
      <div class="search-header border-bottom-1 surface-border flex align-items-center px-4 py-3">
        <i :class="['pi pi-search text-500 text-xl', $i18n.locale === 'ar' ? 'ml-3' : 'mr-3']"></i>
        <input ref="searchInput" v-model="searchQuery" type="text" class="w-full text-lg border-none outline-none text-color"
          style="background: transparent;" :placeholder="$t('form.search') || 'Search pages, actions, or plugins...'"
          @keydown="handleKeydown" />
        <span class="text-500 text-xs font-semibold px-2 py-1 surface-200 border-round">ESC</span>
      </div>

      <div class="results-container py-2" style="max-height: 400px; overflow-y: auto;">
        <div v-if="filteredItems.length === 0" class="text-center py-5 text-500">
          {{ $t('store.no_products') || 'No results found' }} "{{ searchQuery }}"
        </div>

        <ul v-else class="list-none p-0 m-0">
          <li v-for="(item, index) in filteredItems" :key="index"
            :class="['result-item flex align-items-center px-4 py-3 cursor-pointer transition-colors transition-duration-150', { 'surface-hover': selectedIndex === index }]"
            @click="executeItem(item)" @mouseenter="selectedIndex = index">
            <i :class="[item.icon || 'pi pi-file', 'text-500 text-lg', $i18n.locale === 'ar' ? 'ml-3' : 'mr-3']"></i>
            <div class="flex-grow-1">
              <div class="text-900 font-medium">{{ item.label }}</div>
              <div class="text-500 text-sm" v-if="item.category">{{ item.category }}</div>
            </div>
            <i v-if="item.to" class="pi pi-angle-right text-400"></i>
            <i v-else-if="item.command" class="pi pi-bolt text-400"></i>
          </li>
        </ul>
      </div>
    </div>
  </Dialog>
</template>

<script>
import navigation from "@/router/navigation";
import EventBus from "@/AppEventBus";
import app from "@/store/app";

export default {
  name: "CommandPalette",
  data() {
    return {
      visible: false,
      searchQuery: "",
      selectedIndex: 0,
    };
  },
  computed: {
    isRouteAllowed() {
      // Build a flat set of allowed paths from role.route for quick lookup
      const allowed = new Set();
      if (app.role && app.role.route) {
        for (const key in app.role.route) {
          const paths = app.role.route[key];
          if (Array.isArray(paths)) {
            paths.forEach(p => allowed.add(p));
          }
        }
      }
      const allowedArr = [...allowed];
      return (path) => {
        if (!path) return true; // Items without a route (commands, urls) are always shown
        if (app.user && app.user.roleId === "admin") return true;
        return allowed.has(path) || allowedArr.some(a => path.startsWith(a + "/"));
      };
    },
    allItems() {
      const items = [];
      const isAllowed = this.isRouteAllowed;

      // Parse navigation
      for (const [catKey, category] of Object.entries(navigation)) {
        // Skip entire category if it doesn't exist in role.route (non-admin)
        if (app.user && app.user.roleId !== "admin" && app.role && app.role.route && !app.role.route[catKey]) continue;
        if (category.items) {
          for (const [itemKey, item] of Object.entries(category.items)) {
            if (item.items && Array.isArray(item.items)) {
              // Sub items
              item.items.forEach(sub => {
                if (!isAllowed(sub.to)) return;
                items.push({
                  ...sub,
                  category: `${category.label} > ${item.label}`,
                  label: sub.label,
                  icon: sub.icon || item.icon
                });
              });
            } else {
              if (!isAllowed(item.to)) continue;
              items.push({
                ...item,
                category: category.label
              });
            }
          }
        }
      }

      // Add global actions
      items.push({
        label: "Toggle Dark Mode",
        category: "System Actions",
        icon: "pi pi-moon",
        command: () => {
          const dark = !this.$appState.darkTheme;
          EventBus.emit("theme-change", {
            theme: dark ? "lara-dark-indigo" : "lara-light-indigo",
            dark: dark
          });
        }
      });

      return items;
    },
    filteredItems() {
      if (!this.searchQuery) return this.allItems.slice(0, 10);
      
      const query = this.searchQuery.toLowerCase();
      return this.allItems.filter(item => {
        return (item.label && item.label.toLowerCase().includes(query)) || 
               (item.category && item.category.toLowerCase().includes(query));
      }).slice(0, 10);
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.searchQuery = "";
        this.selectedIndex = 0;
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    searchQuery() {
      this.selectedIndex = 0;
    }
  },
  mounted() {
    window.addEventListener("keydown", this.globalKeydownListener);
    EventBus.on("open-command-palette", this.openPalette);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.globalKeydownListener);
    EventBus.off("open-command-palette", this.openPalette);
  },
  methods: {
    openPalette() {
      this.visible = true;
    },
    globalKeydownListener(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        this.visible = !this.visible;
      }
    },
    handleKeydown(e) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (this.selectedIndex < this.filteredItems.length - 1) {
          this.selectedIndex++;
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (this.selectedIndex > 0) {
          this.selectedIndex--;
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (this.filteredItems.length > 0) {
          this.executeItem(this.filteredItems[this.selectedIndex]);
        }
      } else if (e.key === "Escape") {
        this.visible = false;
      }
    },
    executeItem(item) {
      this.visible = false;
      if (item.to) {
        this.$router.push(item.to);
      } else if (item.command) {
        item.command();
      }
    }
  }
};
</script>

<style>
/* Global override for this specific dialog to enforce Clean Light Product design system */
.command-palette-dialog .p-dialog-content {
  padding: 0 !important;
  border-radius: 12px;
}
.command-palette-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0px 3px 5px rgba(0,0,0,.02), 0px 0px 2px rgba(0,0,0,.05), 0px 1px 4px rgba(0,0,0,.08) !important;
  border: 1px solid var(--surface-border);
}
</style>
